<template>
  <router-view />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { getAuthClient } from '@/plugins/auth';
import { RouteNames } from '@/router/routeNames';
import { mapState, mapStores, mapActions } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { getBootstrapTasks, getNextBootstrapRoute } from '@/modules/login/login.utils';

export default defineComponent({
  name: 'PostLogin',
  setup() {
    const { locale } = useI18n();
    const changeLanguage = (newLocale: string) => {
      // en-US en_US
      newLocale = newLocale.replace('_', '-');
      locale.value = newLocale;
    };

    return {
      changeLanguage,
    };
  },
  data() {
    return {
      error: '',
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useCustomerStore, ['customerProfile']),
  },
  methods: {
    ...mapActions(useGlobalStore, ['changeLocale']),
    async getAuthTokens() {
      const urlParams = new URLSearchParams(window.location.search);
      const queryEntries = Object.fromEntries(urlParams.entries());
      return CapacitorFacade.isNativePlatform()
        ? getAuthClient().getAccessToken()
        : getAuthClient().handleLoginCallback(queryEntries);
    },
    async processPostLoginTasks() {
      // determine next route
      const tasks = await getBootstrapTasks();
      const nextRoute = await getNextBootstrapRoute(tasks);
      const underscoreToDash = this.customerProfile.preferredLang.replace('_', '-');
      this.changeLanguage(underscoreToDash);
      await this.$router.push(nextRoute);
    },
  },
  async updated() {
    // TODO: check if authenticated, sometimes redirect happens before the full login process is resolved
    // ! in mobile ios, login callback is handled in the login call (see auth/index.js)
    if (CapacitorFacade.isNativePlatform()) await this.processPostLoginTasks();
  },
  async created() {
    // TODO: add a loading ui
    // TODO: handle when there are no query params
    // ? validate query params
    // ? if valid, get tokens
    // ? if error, handle, maybe redirect to login, or display error
    // TODO: refacctor error handling

    // in web, postlogin is handled throught redirect
    if (!CapacitorFacade.isNativePlatform()) {
      const { code, state, error, errorCode } = this.$route.query;
      if (code && state) {
        // TODO: validate if saved state is the same as response state
        try {
          await this.getAuthTokens();
        } catch (e) {
          this.error = `ERROR: ${e}`;
          const gs = useGlobalStore();
          gs.addError({
            error: { key: String(e), response: String(e), message: String(e) },
            context: 'Login Callback',
          });
          await this.$router.push({ name: RouteNames.ERROR });
        }
        // process post login task
        await this.processPostLoginTasks();
      } else if (errorCode) {
        this.error = 'Something wrong happened';
        if (error || errorCode) {
          this.error += `${error}:${errorCode}`;
        }
        const gs = useGlobalStore();
        gs.addError({
          error: {
            key: String(errorCode),
            response: String(errorCode),
            message: String(errorCode),
          },
          context: 'Login Callback',
        });
        await this.$router.push({ name: RouteNames.ERROR });
      }
    } else {
      // getAuthClient;
      const isAuthenticated = await getAuthClient().isAuthenticated();
      // TODO: handle error or log in DT
      if (isAuthenticated) {
        await this.processPostLoginTasks();
      } else {
        // TODO: handle this scenario
      }
    }
  },
});
</script>
